import React, { useState } from 'react';
import lodashCompact from 'lodash/fp/compact';

import capitalize from 'common/util/capitalize';
import { DATA_TABLE_SOURCE_ADDRESS_RESOLUTION } from 'common/dataTableConstants';
import {
  ADDRESS_RESOLUTION_STATUS_INVALID_MATCH,
  ADDRESS_RESOLUTION_STATUS_NO_MATCH,
  ADDRESS_RESOLUTION_RECOVERABLE_SOURCES,
  ADDRESS_RESOLUTION_STATUS_TO_LABEL_MAPPING,
} from 'common/utility/constants';

import SrBox from 'client/components/SrBox';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrDataTableUI from 'client/components/SrDataTableUI';
import { SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';
import SrText from 'client/components/SrText';
import UPAddressResolutionDetailsModal from 'client/components/UPAddressResolutionDetailsModal/UPAddressResolutionDetailsModal';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import SrButton, {
  SR_BUTTON_HEIGHT_SHORT,
  SR_BUTTON_THEME_ADOBE_YELLOW,
} from '../SrButton/SrButton';

const NO_MATCH_COUNT = 20;

const formatDate = (date) => {
  return momentWithAustinTimezone(date).format('MMM D, YYYY');
};

const dataTableHeaders = [
  {
    content: 'Created Date',
    dataTableFieldId: 'createdAt',
    sortPosition: 'right',
    style: { width: '120px' },
  },
  {
    content: 'Partner',
    dataTableFieldId: 'partnerName',
    sortPosition: 'right',
    style: { width: '160px' },
  },
  {
    content: 'Search Term',
    sortPosition: 'right',
    style: { width: '360px' },
  },
  {
    content: 'Status',
    sortPosition: 'right',
    style: { width: '160px' },
  },
  {
    content: 'Source',
    sortPosition: 'right',
    style: { width: '160px' },
  },
  {
    content: '',
    sortPosition: 'right',
    style: { width: '160px' },
  },
];

export function getDataTableSettingsFromProps() {
  return {
    dataSource: DATA_TABLE_SOURCE_ADDRESS_RESOLUTION,
    rootViewId: DATA_TABLE_SOURCE_ADDRESS_RESOLUTION,
    initialViewSettings: {
      sort: {
        createdAt: 'desc',
      },
      filter: {
        status: [ADDRESS_RESOLUTION_STATUS_NO_MATCH, ADDRESS_RESOLUTION_STATUS_INVALID_MATCH],
        source: ADDRESS_RESOLUTION_RECOVERABLE_SOURCES,
      },
    },
    count: NO_MATCH_COUNT,
    itemsPropName: 'items',
    resetOnUnmount: true,
  };
}

const buildRows = (items, onResolveClick) => {
  return items.map((item) => {
    return {
      cells: [
        {
          content: (
            <SrText font="greycliff" weight="700">
              {formatDate(item.createdAt)}
            </SrText>
          ),
        },
        {
          content: (
            <SrText font="greycliff" weight="700">
              {item.partnerName}
            </SrText>
          ),
        },
        {
          content: (
            <SrText font="greycliff" weight="600">
              {item.searchTerm}
            </SrText>
          ),
          style: {
            maxWidth: '340px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
        {
          content: (
            <SrText font="greycliff" weight="600">
              {ADDRESS_RESOLUTION_STATUS_TO_LABEL_MAPPING[item.status]}
            </SrText>
          ),
        },
        {
          content: (
            <SrText font="greycliff" weight="600">
              {capitalize(item.source)}
            </SrText>
          ),
        },
        {
          content: (
            <SrButton
              heightType={SR_BUTTON_HEIGHT_SHORT}
              theme={SR_BUTTON_THEME_ADOBE_YELLOW}
              onClick={() => onResolveClick(item)}
            >
              Process
            </SrButton>
          ),
        },
      ],
    };
  });
};

const buildLoadMoreRow = ({ dataTableIsFullyLoaded, dataTableApiState, loadNextPage }) => {
  if (dataTableIsFullyLoaded) {
    return null;
  }

  return {
    onClick: () => (dataTableApiState.inProgress ? null : loadNextPage()),
    cells: [
      {
        content: (
          <div style={{ height: '100%', textAlign: 'center', backgroundColor: '#F5F9FF' }}>
            <SrText lineHeight="50px" font="greycliff" weight="800" size="16" color="blue300">
              View more items
            </SrText>
          </div>
        ),
        colSpan: dataTableHeaders.length,
        noPadding: true,
      },
    ],
  };
};

const UPAddressResolutionNoMatchDataTable = () => {
  const [selectedItem, setSelectedItem] = useState(null);

  const onResolveClick = (item) => {
    setSelectedItem(item);
  };

  const onCloseModal = () => {
    setSelectedItem(null);
  };

  return (
    <>
      <SrDataTableConnected {...getDataTableSettingsFromProps()}>
        {({
          items,
          dataTableApiState,
          viewSettings,
          loadNextPage,
          dataTableIsFullyLoaded,
          reloadItems,
        }) => {
          if (dataTableApiState.inProgress && items.length === 0) {
            return (
              <div>
                <div className="loading-ellipses clearfix" />
              </div>
            );
          }

          const rows = buildRows(items, onResolveClick);
          const loadMoreRow = buildLoadMoreRow({
            dataTableIsFullyLoaded,
            dataTableApiState,
            loadNextPage,
          });

          return (
            <SrBox marginTop="20px" paddingBottom="56px">
              <div>
                <SrDataTableUI
                  sticky
                  theme={SR_TABLE_THEME_PARTNER_CLEAR}
                  dataTableViewSettings={viewSettings}
                  headers={dataTableHeaders}
                  maxWidth="100%"
                  rows={lodashCompact([...rows, loadMoreRow])}
                />
                {dataTableApiState.inProgress && (
                  <div>
                    <div className="loading-ellipses clearfix" />
                  </div>
                )}
              </div>
              {items.length === 0 && dataTableIsFullyLoaded && (
                <SrBox
                  backgroundColor="#F5F9FF"
                  display="flex"
                  justifyContent="center"
                  flexDirection="column"
                  alignItems="center"
                  padding="20px"
                  opacity="0.8"
                  cursor="default"
                >
                  <SrText font="greycliff" weight="500" fontSize="16px">
                    No address resolution attempts found.
                  </SrText>
                </SrBox>
              )}
              {selectedItem && (
                <UPAddressResolutionDetailsModal
                  item={selectedItem}
                  onClose={onCloseModal}
                  reloadItems={reloadItems}
                />
              )}
            </SrBox>
          );
        }}
      </SrDataTableConnected>
    </>
  );
};

UPAddressResolutionNoMatchDataTable.displayName = 'UPAddressResolutionNoMatchDataTable';

export default UPAddressResolutionNoMatchDataTable;
